import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import * as RDialog from '@radix-ui/react-dialog'
import { api } from '../lib/api'
import { keys, useProjects } from '../lib/queries'
import { formatDue, priorityMark } from '../lib/format'
import { TaskKey } from './TaskKey'
import { Avatar, Spinner, cx } from './ui'

/**
 * Quick search. Typing a key like SHOP-142 jumps straight to that task, which
 * is the fastest path from a Slack message to the thing it references.
 */
export function CommandPalette({
  open, onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const [query, setQuery] = useState('')
  const [debounced, setDebounced] = useState('')
  const [cursor, setCursor] = useState(0)
  const navigate = useNavigate()
  const projects = useProjects()
  const listRef = useRef<HTMLDivElement>(null)

  // Reset between openings so the palette never shows a stale result set.
  useEffect(() => {
    if (open) {
      setQuery('')
      setDebounced('')
      setCursor(0)
    }
  }, [open])

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(query.trim()), 130)
    return () => window.clearTimeout(timer)
  }, [query])

  const tasks = useQuery({
    queryKey: keys.search(debounced),
    queryFn: () => api.search(debounced),
    enabled: open && debounced.length > 0,
    staleTime: 5_000,
  })

  const projectMatches = useMemo(() => {
    if (!debounced) return []
    const needle = debounced.toLowerCase()
    return (projects.data ?? [])
      .filter((p) => !p.archivedAt)
      .filter((p) => p.name.toLowerCase().includes(needle) || p.key.toLowerCase().includes(needle))
      .slice(0, 3)
  }, [projects.data, debounced])

  const results = useMemo(
    () => [
      ...projectMatches.map((p) => ({ kind: 'project' as const, project: p })),
      ...(tasks.data ?? []).map((t) => ({ kind: 'task' as const, task: t })),
    ],
    [projectMatches, tasks.data],
  )

  useEffect(() => setCursor(0), [results.length])

  function go(index: number) {
    const item = results[index]
    if (!item) return
    onOpenChange(false)
    if (item.kind === 'project') navigate(`/p/${item.project.key}`)
    else navigate(`/p/${item.task.projectKey}/t/${item.task.key}`)
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setCursor((c) => Math.min(c + 1, results.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setCursor((c) => Math.max(c - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      go(cursor)
    }
  }

  // Keep the highlighted row in view when arrowing past the fold.
  useEffect(() => {
    listRef.current?.querySelector('[data-active="true"]')?.scrollIntoView({ block: 'nearest' })
  }, [cursor])

  return (
    <RDialog.Root open={open} onOpenChange={onOpenChange}>
      <RDialog.Portal>
        <RDialog.Overlay className="fade-in fixed inset-0 z-40 bg-black/35 backdrop-blur-[2px]" />
        <RDialog.Content
          onKeyDown={onKeyDown}
          className={cx(
            'fade-in fixed left-1/2 top-[12vh] z-50 w-[calc(100vw-2rem)] max-w-[560px] -translate-x-1/2',
            'overflow-hidden rounded-lg border border-[var(--line)] bg-[var(--surface)]',
            'shadow-2xl shadow-black/15 dark:shadow-black/60',
          )}
        >
          <RDialog.Title className="sr-only">Search tasks and projects</RDialog.Title>
          <RDialog.Description className="sr-only">
            Type to find a task by key, title or description.
          </RDialog.Description>

          <div className="flex items-center gap-2.5 border-b border-[var(--line)] px-3.5">
            <span className="text-[var(--ink-3)]">
              <SearchGlyph />
            </span>
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Jump to a task or project…"
              className="w-full bg-transparent py-3 text-[14px] outline-none placeholder:text-[var(--ink-3)]"
            />
            {tasks.isFetching && <Spinner className="text-[var(--ink-3)]" />}
          </div>

          <div ref={listRef} className="max-h-[52vh] overflow-y-auto py-1.5">
            {!debounced && (
              <p className="px-3.5 py-6 text-center text-[13px] text-[var(--ink-3)]">
                Search by task key, title or anything in a description.
              </p>
            )}

            {debounced && results.length === 0 && !tasks.isFetching && (
              <p className="px-3.5 py-6 text-center text-[13px] text-[var(--ink-3)]">
                Nothing matches “{debounced}”.
              </p>
            )}

            {results.map((item, i) => (
              <button
                key={item.kind === 'project' ? item.project.id : item.task.id}
                data-active={i === cursor}
                onMouseEnter={() => setCursor(i)}
                onClick={() => go(i)}
                className={cx(
                  'flex w-full items-center gap-2.5 px-3.5 py-2 text-left transition-colors duration-[90ms]',
                  i === cursor ? 'bg-[var(--sunken)]' : 'bg-transparent',
                )}
              >
                {item.kind === 'project' ? (
                  <>
                    <span
                      aria-hidden
                      className="h-2.5 w-2.5 shrink-0 rounded-[3px]"
                      style={{ background: item.project.color }}
                    />
                    <span className="truncate text-[13px]">{item.project.name}</span>
                    <span className="task-key ml-auto text-[11px] text-[var(--ink-3)]">
                      board
                    </span>
                  </>
                ) : (
                  <>
                    <TaskKey taskKey={item.task.key} className="w-[86px] shrink-0 text-[var(--ink-2)]" />
                    <span className="min-w-0 flex-1 truncate text-[13px]">
                      {priorityMark(item.task.priority) && (
                        <span className="task-key mr-1.5 font-semibold text-[var(--danger)]">
                          {priorityMark(item.task.priority)}
                        </span>
                      )}
                      {item.task.title}
                    </span>
                    {item.task.dueDate && (
                      <span className="task-key shrink-0 text-[11px] text-[var(--ink-3)]">
                        {formatDue(item.task.dueDate)}
                      </span>
                    )}
                    <span className="w-[70px] shrink-0 truncate text-right text-[11px] text-[var(--ink-3)]">
                      {item.task.statusName}
                    </span>
                    <Avatar user={item.task.assignee} size={18} />
                  </>
                )}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-3 border-t border-[var(--line)] px-3.5 py-1.5 text-[11px] text-[var(--ink-3)]">
            <Hint keys="↑↓" label="navigate" />
            <Hint keys="↵" label="open" />
            <Hint keys="esc" label="close" />
          </div>
        </RDialog.Content>
      </RDialog.Portal>
    </RDialog.Root>
  )
}

function Hint({ keys: k, label }: { keys: string; label: string }) {
  return (
    <span className="flex items-center gap-1">
      <kbd className="task-key rounded border border-[var(--line)] px-1 text-[10px]">{k}</kbd>
      {label}
    </span>
  )
}

function SearchGlyph() {
  return (
    <svg
      width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="1.5" strokeLinecap="round" aria-hidden
    >
      <circle cx="7" cy="7" r="4.25" />
      <path d="M10.2 10.2 13.5 13.5" />
    </svg>
  )
}
